import { Table } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  convertISOToDateFormat,
  convertISOToDateTimeFormat
} from '../../../../util/timezone';
import { MultiDataColumn, MultiHeader } from '../../../common/components';
import { PreventionAlertHistoryContainer } from '../../ScoreExplainability/styled';
import style from './index.scss';
import { connect } from 'react-redux';
import { getAMLSCommonUnifiedAlertsPrevention } from '../../../../redux/actions/common';
import { fetchTypologiesActions } from '../../../../redux/actions/common';
import { PROD_IDS } from '../../../common/AlertHistory/FilterOption/constants';
import TablePagination from '../../../cm/common/TablePagination';
import {
  UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION,
  UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION_PRODUCTION
} from '../../../trm/testRunInstanceDashboard/ResultTab/constants';
import DateRangeWithPresets from '../../../cm/common/DateRangeWithPresets';
import { fromDaysToRangeBack } from '../../../cm/utils/common';
import { toLower } from 'lodash';

const { from, to } = fromDaysToRangeBack(30);

const AlertHistoryPrevention = ({
  dispatch,
  alertPartyIdentity,
  alertHistory,
  trmActions,
  isLoading,
  isFromCM
}) => {
  const [query, setQuery] = useState({
    from,
    to,
    currentPage: 1,
    pageSize: 10
  });

  useEffect(() => {
    dispatch(fetchTypologiesActions());
  }, []);

  const preventionAlertColors = (key) => {
    return trmActions?.data?.find(
      ({ displayName }) => toLower(displayName) === toLower(key)
    )?.alertLevelColor;
  };

  useEffect(() => {
    if (alertPartyIdentity.data) {
      let uniqueCombinedKeys = new Set();

      Object.keys(alertPartyIdentity.data).forEach((partyIdentity) => {
        const partyKey = alertPartyIdentity.data[partyIdentity].partyKey;

        if (partyKey) {
          uniqueCombinedKeys.add(partyKey);
        }
      });

      if (query.from && query.to && !alertPartyIdentity.isLoading) {
        dispatch(
          getAMLSCommonUnifiedAlertsPrevention({
            customerId: `${[...uniqueCombinedKeys].join(',')},`,
            sourceIds: isFromCM
              ? UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION_PRODUCTION
              : UNIFIED_ALERTS_SOURCE_IDS_FRAUD_PREVENTION,
            limit: query.pageSize,
            offset: (query.currentPage - 1) * query.pageSize,
            from: query.from,
            to: query.to
          })
        );
      }
    }
  }, [query, alertPartyIdentity]);

  const columns = [
    {
      title: 'Alert',
      children: [
        {
          title: <MultiHeader header="Alert ID" subHeaders={['Alert Date']} />,
          ellipsis: true,
          width: '10%',
          render: (record) => {
            const { alertId = '-', alertDate = '-' } = record;
            if (record.alertId && record.alertDate) {
              return (
                <MultiDataColumn.Link
                  pop
                  data={[
                    {
                      text: alertId
                    },
                    {
                      text: convertISOToDateFormat(alertDate) || '-'
                    }
                  ]}
                  customLink={
                    isFromCM
                      ? '/app/cm/alerts/:alertId'
                      : '/app/trm/run-instance/:alertId'
                  }
                  customLinkParam=":alertId"
                  newTab={true}
                />
              );
            }
          }
        },
        {
          title: <MultiHeader header="Alert Source" subHeaders={['']} />,
          width: '7%',
          render: (record) => {
            const { alertSourceId } = record;
            let alertSourceName = '-';
            if (alertSourceId === PROD_IDS.AML_TM.ID) {
              alertSourceName = 'AML-TM';
            } else if (alertSourceId === PROD_IDS.FRAUD.ID) {
              alertSourceName = 'Fraud Detection';
            } else if (
              alertSourceId === PROD_IDS.FRAUD_PREV.ID ||
              alertSourceId === PROD_IDS.RT_FRAUD.ID ||
              alertSourceId === PROD_IDS.FRAUD_PREV_BATCH.ID
            ) {
              alertSourceName = 'Fraud Prevention';
            }
            return (
              <p
                style={{
                  color: '#2a2e39',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                {alertSourceName}
              </p>
            );
          }
        },
        {
          title: <MultiHeader header="No of Variants" subHeaders={['']} />,
          width: '10%',
          render: (record) => {
            return (
              <p
                style={{
                  alignSelf: 'center',
                  color: '#2a2e39',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                {record.triggeredRules?.length || 0}
              </p>
            );
          }
        },
        {
          title: (
            <MultiHeader
              header="Alert Level/Actions"
              subHeaders={['Alert Score/Count']}
            />
          ),
          width: '7%',
          render: (record) => {
            if (record.alertSourceId === '24') {
              let triggeredRulesActionSummary = {};
              try {
                triggeredRulesActionSummary = JSON.parse(
                  record.triggeredRulesActionSummary
                );
              } catch (error) {
                console.log('error in parsing JSON', error);
              }
              return (
                <>
                  {Object.keys(triggeredRulesActionSummary).map((key) => (
                    <div
                      key={key}
                      style={{
                        color: preventionAlertColors(key)
                      }}>
                      {key}: {triggeredRulesActionSummary[key]}
                    </div>
                  ))}
                </>
              );
            }
            if (
              record.alertSourceId === '25' ||
              record.alertSourceId === '29'
            ) {
              const { additionalFraudInfo = '-' } = record;
              const keys = Object.keys(additionalFraudInfo);
              const values = Object.values(additionalFraudInfo);
              return (
                <>
                  {keys.map((key, index) => (
                    <div
                      key={key}
                      style={{
                        color: preventionAlertColors(key)
                      }}>
                      {key}: {values[index]}
                    </div>
                  ))}
                </>
              );
            } else {
              return record.alertLevel;
            }
          }
        }
      ]
    },
    {
      title: 'Variant Names/Typologies Triggered',
      children: [
        {
          title: '',
          width: '10%',
          render: (record) => {
            let typologyNames = record?.triggeredRules?.map((name) => {
              return name.ruleName;
            });

            return Array.isArray(typologyNames)
              ? typologyNames?.map((m, i) => (
                  <p style={{ marginBottom: 0 }} key={`m-${i}`}>
                    {m}
                    {i !== typologyNames.length - 1 ? ',' : ''}
                  </p>
                ))
              : typologyNames?.toString() || '';
          }
        }
      ]
    },
    {
      title: 'Alert Status',
      children: [
        {
          title: <MultiHeader header="Status" subHeaders={['Close Date']} />,
          width: '10%',
          render: ({ alertStatus, closureDate }) => {
            return (
              <MultiDataColumn
                data={[
                  { text: alertStatus || '-' },
                  {
                    text: closureDate
                      ? convertISOToDateTimeFormat(closureDate)
                      : '-'
                  }
                ]}
                pop
              />
            );
          }
        }
      ]
    }
  ];

  const handleChange = (currentPage, pageSize) => {
    setQuery({
      ...query,
      currentPage,
      pageSize
    });
  };

  const onDurationChange = (from, to) => {
    setQuery({
      ...query,
      from,
      to,
      currentPage: 1
    });
  };

  return (
    <PreventionAlertHistoryContainer>
      <div className={style.filters}>
        <DateRangeWithPresets
          from={query?.from}
          to={query?.to}
          isDisableFutureDates
          onChange={onDurationChange}
        />
      </div>
      <Table
        rowKey={({ alertId }) => alertId}
        columns={columns}
        dataSource={alertHistory?.payload || []}
        className={style.alertTable}
        loading={isLoading || alertPartyIdentity?.isLoading}
        pagination={{
          total: alertHistory?.total || 0,
          current: query.currentPage,
          pageSize: query.pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: handleChange,
          showTotal: (total) => (
            <TablePagination
              selectedItemsCount={null}
              total={total}
              currentPage={query.currentPage}
              pageSize={query.pageSize}
            />
          )
        }}
      />
    </PreventionAlertHistoryContainer>
  );
};

AlertHistoryPrevention.propTypes = {
  dispatch: PropTypes.func.isRequired,
  alertPartyIdentity: PropTypes.object.isRequired,
  alertHistory: PropTypes.object,
  isLoading: PropTypes.bool
};

function mapStateToProps(state) {
  const { unifiedAlertsPrevention = {} } = state.amlsCommon;
  const { data: alertHistory, error, isLoading } = unifiedAlertsPrevention;

  return {
    alertHistory,
    error,
    isLoading,
    trmActions: state.amlsCommon.commonTypologyAction
  };
}

export default connect(mapStateToProps)(AlertHistoryPrevention);
